import httpClient from '@xto10x/core/lib/httpClient';
import config from 'utils/config';
import { getIsPeopleCuesOrg } from './userApi';

const objectivesApi = {
  getObjectivesList: requestBody =>
    httpClient.get('/v1/objective', requestBody),

  getObjectiveCycles: requestBody =>
    httpClient.get('/v1/objective-cycle', requestBody),

  lockObjectiveCycle: (cycle_id, lockTime = new Date().toISOString()) =>
    httpClient.put(
      `/v1/objective-cycle/${cycle_id}?objective_cycle_lock_at=${lockTime}`
    ),

  unlockObjectiveCycle: cycle_id =>
    httpClient.put(`/v1/objective-cycle/${cycle_id}?objective_cycle_lock_at=`),

  createObjective: requestBody =>
    httpClient.post('/v1/objective/', requestBody),

  createObjectiveDuplicate: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/duplicate`, requestBody),

  checkIsObjectiveDuplicated: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/is-duplicated`, requestBody),

  getObjectiveDetail: (objective_id, params) =>
    httpClient.get(`/v1/objective/${objective_id}`, params),

  updateObjectiveOwner: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/owner`, requestBody),

  updateObjectiveStatus: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/status`, requestBody),

  updateObjectiveProgress: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/update`, requestBody),

  updateObjectiveCheckIn: (objective_id, checkIn_id, requestBody) =>
    httpClient.put(
      `/v1/objective/${objective_id}/update/${checkIn_id}`,
      requestBody
    ),

  deleteObjectiveCheckIn: (objective_id, checkIn_id) =>
    httpClient.delete(`/v1/objective/${objective_id}/update/${checkIn_id}`),

  updateObjectiveTeams: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/tags`, requestBody),

  updateDueDate: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/due-date`, requestBody),

  updateStartDate: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/start-date`, requestBody),

  updateObjectiveType: (objective_id, requestBody) =>
    httpClient.put(
      `/v1/objective/${objective_id}/markCompanyObjective`,
      requestBody
    ),

  updateObjectiveCycle: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/link-cycle`, requestBody),

  updateObjective: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}`, requestBody),

  getObjectiveActivity: objective_id =>
    httpClient.get(`/v1/objective/${objective_id}/activity`),

  getObjectiveComments: (objective_id, params) =>
    httpClient.get(`/v1/objective/${objective_id}/comment`, params),

  postObjectiveComment: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/comment`, requestBody),

  updateObjectiveComment: (objective_id, commentId, requestBody) =>
    httpClient.put(
      `/v1/objective/${objective_id}/comment/${commentId}`,
      requestBody
    ),

  deleteObjectiveComment: (objective_id, id) =>
    httpClient.delete(`/v1/objective/${objective_id}/comment/${id}`),

  deleteObjective: (objective_id, delete_subtree) =>
    httpClient.delete(
      `/v1/objective/${objective_id}?delete_subtree=${
        delete_subtree ? 'true' : 'false'
      }`
    ),

  getObjectiveAlignment: objective_id =>
    httpClient.get(`/v1/objective/${objective_id}/alignment`),

  getFilteredObjectives: filters =>
    httpClient.get('/v1/objective/filter/v2', filters),

  getFilteredObjectivesWithDemographics: filters =>
    httpClient.get(
      '/v1/dashboard/summary/filtered-objective-summary-dashboard/',
      filters
    ),

  getSearchObjectiveResults: (uri_encoded_search_term, requestParams) =>
    httpClient.get(
      `/v1/objective/search?query=${uri_encoded_search_term}`,
      requestParams
    ),

  alignObjective: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/align`, requestBody),

  postLabelObjective: requestBody => {
    const { objective_id } = requestBody;
    return httpClient.put(`/v1/objective/${objective_id}/label`, requestBody);
  },

  getLinkedObjectives: (objective_id, strategic_linking = false) =>
    httpClient.get(
      `v1/objective/${objective_id}/dependency?strategic_linking=${strategic_linking}`
    ),

  createLinkedObjective: (objective_id, requestBody) =>
    httpClient.post(`v1/objective/${objective_id}/dependency`, requestBody),

  deleteLinkedObjective: (objective_id, requestBody) =>
    httpClient.delete(`v1/objective/${objective_id}/dependency`, requestBody),

  getLinkedToObjectives: (objective_id, strategic_linking = false) =>
    httpClient.get(
      `v1/objective/${objective_id}/dependents${
        strategic_linking ? '?strategic_linking=true' : ''
      }`
    ),

  updateObjectiveMetric: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/metric`, requestBody),

  addObjectiveMetric: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/metric`, requestBody),

  getMetricDashboard: objective_id =>
    httpClient.get(`/v1/objective/${objective_id}/metric-dashboard`),

  deleteMetric: objective_id =>
    httpClient.delete(`/v1/objective/${objective_id}/metric`),

  // this will be used for My objectives, my directs and tags quick filters
  getDashboardData: (action_type, requestBody) =>
    httpClient.get(`/v1/dashboard/${action_type}`, requestBody),

  getDashboardDataWithQuickFilters: (action_type, requestBody) =>
    httpClient.get(`/v1/dashboard/advanced/${action_type}`, requestBody),

  getDashboardDataWithQuickFiltersV2: requestBody =>
    httpClient.get(
      '/v1/dashboard/advanced/objective-summary-dashboard',
      requestBody
    ),

  getDashboardFilterObjectives: filters =>
    httpClient.get('/v1/objective/dashboard-filter', filters),

  getDashboardFilterObjectivesForQuickFilters: filters =>
    httpClient.get('/v1/objective/advanced-dashboard-filter', filters),

  reorderObjectives: (objective_id, requestBody) =>
    httpClient.put(`/v1/objective/${objective_id}/re-order`, requestBody),

  addReaction: (emoji_id, requestBody) =>
    httpClient.post(`/v1/reaction/${emoji_id}/add`, requestBody),

  removeReaction: (emoji_id, requestBody) =>
    httpClient.post(`/v1/reaction/${emoji_id}/remove`, requestBody),

  getObjectiveContext: objective_id =>
    httpClient.get(`/v1/objective/${objective_id}/context`),

  getTreeKRCount: objective_id =>
    httpClient.get(`/v1/objective/${objective_id}/kr-count`),

  getFilterAttributes: requestBody =>
    httpClient.get('v1/objective/filter-attributes', requestBody),

  getQuickFilterObjectives: requestBody =>
    httpClient.get('v1/objective/advanced-filter', requestBody),

  getOrgHierarchy: user_id =>
    httpClient.get(
      `${config.blitzApiUrl}/v1/blitz/users/${user_id}/hierarchy-users`,
      {},
      {},
      {},
      getIsPeopleCuesOrg()
    ),

  updateManagerApprovalStatus: (objective_id, requestBody) =>
    httpClient.put(
      `/v1/objective/${objective_id}/manager-approval-status`,
      requestBody
    ),

  requestManagerApproval: (objective_id, requestBody) =>
    httpClient.post(
      `/v1/objective/${objective_id}/manager-approval-request`,
      requestBody
    ),

  createObjectiveAndKeyResultsWithCheckIns: requestBody =>
    httpClient.post('v1/objective/okrs-with-checkins', requestBody),

  bulkApproveAllObjectives: requestBody =>
    httpClient.put('v1/objective/bulk-manager-approval', {}, requestBody),

  getSubtreeManagerApprovalCount: objective_id =>
    httpClient.get(
      `/v1/objective/${objective_id}/sub-tree-manager-approval-count`
    ),

  approveObjectiveAndSubtree: objective_id =>
    httpClient.put(`/v1/objective/${objective_id}/sub-tree-manager-approval`),

  updateAutoRollUpAttributes: (objective_id, requestBody) =>
    httpClient.post(`/v1/objective/${objective_id}/auto-rollup`, requestBody),

  acknowledgeKRChangeForAutoRollUp: (objective_id, version) =>
    httpClient.post(
      `/v1/objective/${objective_id}/auto-rollup/acknowledge?version=${version}`
    ),

  getObjectivesSummary: requestBody =>
    httpClient.get(
      'v1/dashboard/summary/objective-summary-dashboard',
      requestBody
    )
};

export default objectivesApi;
